import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import BrainSyncLoader from '../components/BrainSyncLoader';
import AppLanding from './AppLanding';
import LandingPageDesktop from './LandingPageDesktop';
import EmailAuthSheet from '../components/EmailAuthSheet';
import { captureUtmParams } from '../lib/analytics';
import { PROMO_ACTIVE, PROMO_BADGE, PROMO_LOCK_LINE, PROMO_LOCK_SHORT, PROMO_URGENCY, PRICING, ANNUAL_SAVING_PCT } from '../config/promo';
import { 
    Bookmark, Sparkles, Search, Globe, Brain, Tag, 
    Smartphone, ArrowRight, Play, FileText, MessageCircle,
    Video, UtensilsCrossed, Zap, Shield, ChevronRight,
    Folder, Plus, CheckCircle2
} from 'lucide-react';

// Rotating words for hero - singular to match the "every ___" verb.
const ROTATING_WORDS = ['video', 'article', 'recipe', 'post'];

// Detect if we're inside a standalone shell (Android TWA / installed PWA /
// iOS home-screen web app). We check every reasonable signal and CACHE
// a positive result in sessionStorage - once we know we're in the TWA,
// we treat every subsequent render in the session as standalone even if
// display-mode / referrer flake out on a specific navigation. Without
// this cache, users can end up seeing the marketing mobile-web landing
// (with all the below-the-fold sections) inside the app after any
// client-side navigation that loses the android-app:// referrer.
const detectStandalone = () => {
    if (typeof window === 'undefined') return false;
    try {
        if (window.sessionStorage && window.sessionStorage.getItem('bs_is_twa') === '1') {
            return true;
        }
    } catch (_) { /* private mode / storage disabled */ }

    let hit = false;
    try {
        if (typeof window.matchMedia === 'function' && (
            window.matchMedia('(display-mode: standalone)').matches
            || window.matchMedia('(display-mode: minimal-ui)').matches
            || window.matchMedia('(display-mode: fullscreen)').matches
        )) hit = true;
    } catch (_) { /* ignore */ }

    if (!hit && typeof window.navigator !== 'undefined' && window.navigator.standalone === true) {
        hit = true;
    }

    if (!hit && typeof document !== 'undefined') {
        const ref = document.referrer || '';
        if (ref.indexOf('android-app://') === 0) hit = true;
    }

    // Android WebView UA marker (`; wv)`) combined with Android UA. This
    // is the last-resort signal for TWAs where Chrome dropped the
    // display-mode media query on a subsequent SPA route. Regular
    // Chrome tabs do NOT contain `; wv)`, so this stays TWA-only.
    if (!hit && typeof navigator !== 'undefined') {
        const ua = navigator.userAgent || '';
        if (/Android/i.test(ua) && /; wv\)/i.test(ua)) hit = true;
    }

    // URL param (?src=twa or ?tw=1) - lets us force the standalone
    // experience via manifest.json start_url or deep-links.
    if (!hit && typeof window !== 'undefined') {
        try {
            const sp = new URLSearchParams(window.location.search);
            if (sp.get('src') === 'twa' || sp.get('tw') === '1') hit = true;
        } catch (_) { /* ignore */ }
    }

    if (hit) {
        try { window.sessionStorage && window.sessionStorage.setItem('bs_is_twa', '1'); } catch (_) { /* ignore */ }
    }
    return hit;
};

const RotatingWord = () => {
    const [index, setIndex] = useState(0);
    useEffect(() => {
        const timer = setInterval(() => setIndex(i => (i + 1) % ROTATING_WORDS.length), 2400);
        return () => clearInterval(timer);
    }, []);
    // Reserve the width of the longest word so the heading doesn't
    // re-flow on every tick. We pad the reserved height generously so
    // descenders ('p', 'g') and the cap top of the emerald word never
    // get clipped at the desktop text-7xl size.
    const longest = ROTATING_WORDS.reduce((a, b) => a.length >= b.length ? a : b);
    return (
        <span className="relative inline-block text-emerald-600 align-baseline">
            {/* Invisible longest word reserves width + height */}
            <span className="invisible select-none" aria-hidden="true">{longest}</span>
            {/* Animated word overlaid - cross-fade only, no Y translate
                + no overflow:hidden. Eliminates the clipping the slot-
                machine mask introduced at large heading sizes. */}
            <span className="absolute inset-0 flex items-center justify-center">
                <AnimatePresence mode="wait">
                    <motion.span
                        key={ROTATING_WORDS[index]}
                        className="block leading-[1]"
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    >
                        {ROTATING_WORDS[index]}
                    </motion.span>
                </AnimatePresence>
            </span>
        </span>
    );
};

// Simplified Dashboard Mockup matching the reference
const MOCK_BOOKMARKS = [
    { 
        title: 'Creamy Mushroom Risotto', source: 'tiktok.com',
        summary: 'A classic Italian risotto with porcini mushrooms, finished...',
        tags: ['recipe', 'italian'], color: '#F97316'
    },
    { 
        title: 'How to Build a Second Brain', source: 'youtube.com',
        summary: "Tiago Forte explains his PARA method for digital...",
        tags: ['productivity', 'PKM'], color: '#EF4444'
    },
];

const DashboardMockup = () => (
    <div className="relative w-full max-w-lg mx-auto" data-testid="dashboard-mockup">
        <div className="absolute -inset-8 rounded-3xl opacity-30 blur-3xl pointer-events-none"
            style={{ background: 'radial-gradient(ellipse at 50% 40%, rgba(16,185,129,0.2), transparent 70%)' }} />

        <div className="relative bg-white rounded-2xl shadow-[0_20px_80px_-20px_rgba(0,0,0,0.12)] border border-gray-200/80 overflow-hidden">
            {/* Chrome bar */}
            <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                </div>
                <div className="flex-1 flex items-center justify-center">
                    <div className="bg-white rounded-lg border border-gray-200 px-4 py-1.5 text-xs text-gray-400 font-mono w-full max-w-[220px] text-center">
                        brainsync.app/dashboard
                    </div>
                </div>
            </div>

            {/* App content */}
            <div className="p-4 sm:p-5 bg-[#FAFAFA]">
                {/* Search bar */}
                <div className="flex items-center gap-2 mb-4">
                    <div className="flex-1 flex items-center gap-2.5 bg-white rounded-xl border border-gray-100 px-3.5 py-2.5">
                        <Search className="w-4 h-4 text-gray-300" />
                        <span className="text-sm text-gray-300">Search bookmarks...</span>
                    </div>
                    <div className="w-9 h-9 rounded-xl bg-[#2E3A2F] flex items-center justify-center shrink-0">
                        <Plus className="w-4 h-4 text-white" />
                    </div>
                </div>

                {/* Cards */}
                <div className="grid grid-cols-2 gap-3">
                    {MOCK_BOOKMARKS.map((bm, i) => (
                        <motion.div
                            key={bm.title}
                            className="bg-white rounded-xl border border-gray-100 p-3"
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6 + i * 0.15, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                        >
                            <div className="h-1 rounded-full mb-2.5 opacity-70" style={{ backgroundColor: bm.color, width: '45%' }} />
                            <p className="text-xs sm:text-sm font-bold text-gray-900 leading-snug mb-0.5">{bm.title}</p>
                            <p className="text-[10px] sm:text-xs text-gray-400 font-mono mb-1.5">{bm.source}</p>
                            <p className="text-[10px] sm:text-xs text-gray-400 leading-relaxed mb-2 line-clamp-2">{bm.summary}</p>
                            <div className="flex flex-wrap gap-1">
                                {bm.tags.map(t => (
                                    <span key={t} className="px-2 py-0.5 rounded-full bg-gray-50 border border-gray-100 text-gray-500 text-[10px] font-mono">{t}</span>
                                ))}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    </div>
);

// Feature card
const FeatureCard = ({ icon: Icon, title, description, className = '', accent = false, delay = 0 }) => (
    <motion.div
        className={`relative overflow-hidden rounded-2xl border transition-all duration-300 hover:shadow-[0_8px_30px_-12px_rgba(0,0,0,0.12)] ${
            accent 
                ? 'bg-[#2E3A2F] text-white border-[#2E3A2F] p-6 sm:p-8' 
                : 'bg-white border-gray-100 p-5 sm:p-6 hover:border-emerald-200'
        } ${className}`}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-40px' }}
        transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
    >
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${
            accent ? 'bg-white/10' : 'bg-emerald-50'
        }`}>
            <Icon className={`w-5 h-5 ${accent ? 'text-emerald-300' : 'text-emerald-600'}`} />
        </div>
        <h3 className={`font-heading text-base sm:text-lg font-semibold mb-1.5 ${accent ? 'text-white' : 'text-gray-900'}`}>{title}</h3>
        <p className={`text-sm leading-relaxed ${accent ? 'text-white/60' : 'text-gray-500'}`}>{description}</p>
    </motion.div>
);

// Stat counter
const StatItem = ({ value, label, delay = 0 }) => (
    <motion.div 
        className="text-center"
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay, duration: 0.4 }}
    >
        <div className="font-heading text-3xl sm:text-4xl font-bold text-[#2E3A2F]">{value}</div>
        <div className="text-xs sm:text-sm text-gray-400 mt-1">{label}</div>
    </motion.div>
);


const LandingPage = () => {
    const { login, user, loading, trySilentSignIn, checkAuth } = useAuth();
    const navigate = useNavigate();
    // True while we're attempting an aggressive session recovery for a
    // user who was previously signed in. Keeps the loader on-screen and
    // prevents the AppLanding from flashing during the recovery window.
    const [recovering, setRecovering] = useState(false);
    const recoveryStarted = useRef(false);
    // Email/password auth sheet - opened from the secondary CTA links.
    // Mode (`register` vs `login`) is set when the user clicks.
    const [emailSheetOpen, setEmailSheetOpen] = useState(false);
    const [emailSheetMode, setEmailSheetMode] = useState('register');

    // Capture ?ref= on first visit so we can attribute the signup after OAuth.
    // Also capture ?next= so we can return the user to the page they came
    // from after auth (e.g. /c/{id} for "Sign up to save this collection"),
    // and auto-open the email auth sheet when ?signup=1 is present.
    useEffect(() => {
        try {
            // Stash any UTM params (e.g. from Google Ads landing URLs)
            // so they survive OAuth round-trips and can be attached to
            // the eventual sign_up event.
            captureUtmParams();
            const params = new URLSearchParams(window.location.search);
            const ref = params.get('ref');
            if (ref && /^user_[a-z0-9]+$/i.test(ref)) {
                localStorage.setItem('brainsync_ref', ref);
            }
            // ?next=/c/xxx - stash for the post-auth redirect. Only accept
            // same-origin relative paths to prevent open-redirect abuse.
            const next = params.get('next');
            if (next && next.startsWith('/') && !next.startsWith('//')) {
                sessionStorage.setItem('brainsync_post_auth_next', next);
            }
            // ?signup=1 - auto-open the email auth sheet in register mode
            // so the user can sign up in-place without a second tap.
            if (params.get('signup') === '1') {
                setEmailSheetMode('register');
                setEmailSheetOpen(true);
            } else if (params.get('login') === '1') {
                setEmailSheetMode('login');
                setEmailSheetOpen(true);
            } else if (params.get('google') === '1') {
                // ?google=1 - trigger Google OAuth immediately (from new
                // desktop landing's primary CTA). Strip the param first so
                // browser back-button doesn't retrigger the OAuth flow.
                try {
                    window.history.replaceState({}, '', window.location.pathname);
                } catch (_) { /* ignore */ }
                login();
            }
        } catch (_) { /* non-browser env */ }
    }, []);

    // Listen for messages from the desktop landing iframe. Two message
    // types today:
    //   { type: 'brainsync-open-auth', mode: 'register' | 'login' }
    //     → opens the EmailAuthSheet inline (no full-page reload).
    //   { type: 'brainsync-save-starter', starterId: 'starter-xxx' }
    //     → stashes the starter id in sessionStorage so Dashboard clones
    //       it after Google auth completes. The iframe itself
    //       navigates the top window to /?google=1 to kick off OAuth.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const onMessage = (event) => {
            // Same-origin only — the iframe is served from the same host.
            if (event.origin !== window.location.origin) return;
            const data = event.data;
            if (!data || typeof data !== 'object') return;
            if (data.type === 'brainsync-open-auth') {
                const mode = data.mode === 'login' ? 'login' : 'register';
                setEmailSheetMode(mode);
                setEmailSheetOpen(true);
            } else if (data.type === 'brainsync-save-starter' && data.starterId) {
                try {
                    sessionStorage.setItem('bs_pending_starter', JSON.stringify({
                        starterId: data.starterId,
                        ts: Date.now(),
                    }));
                } catch (_) { /* ignore quota errors */ }
                // Do NOT open the auth sheet — iframe kicks off Google
                // OAuth via top-level nav to /?google=1 right after
                // posting this message.
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, []);

    // Redirect authenticated users to dashboard (or to the stashed
    // ?next= path if they came from a public collection's "Sign up to
    // save" CTA), or try silent sign-in for returning users.
    useEffect(() => {
        if (loading) return;
        if (user) {
            // Pull the post-auth target out of sessionStorage - set by
            // either this page's mount effect or any other page that
            // wants to bounce a user through auth and back. Consume
            // (delete) it so a fresh visit doesn't replay the redirect.
            let next = '/dashboard';
            try {
                const stashed = sessionStorage.getItem('brainsync_post_auth_next');
                if (stashed && stashed.startsWith('/') && !stashed.startsWith('//')) {
                    next = stashed;
                }
                sessionStorage.removeItem('brainsync_post_auth_next');
            } catch (_) { /* ignore */ }
            navigate(next, { replace: true });
            return;
        }
        const isStandalone = detectStandalone();

        // Standalone (TWA) recovery path. On cold start, the WebView's
        // cookie store sometimes hasn't propagated the session cookie by
        // the time AuthContext fires its first /api/auth/me request, so
        // the user briefly appears logged-out and we'd flash the marketing
        // hero. Detect this case via the `brainsync_user_email` marker
        // (cleared on logout) and retry checkAuth a few more times
        // aggressively before falling through to the real logged-out view.
        if (isStandalone && !recoveryStarted.current) {
            const savedEmail = localStorage.getItem('brainsync_user_email');
            if (savedEmail) {
                recoveryStarted.current = true;
                setRecovering(true);
                (async () => {
                    for (let i = 0; i < 4 && !recoveryStarted.cancelled; i++) {
                        const recovered = await checkAuth({
                            silent: true,
                            maxRetries: 1,
                            timeoutMs: 6000,
                        });
                        if (recovered) return; // useEffect will navigate on next tick
                        await new Promise(r => setTimeout(r, 700));
                    }
                    // Truly logged out - drop the email marker so subsequent
                    // launches go straight to the landing without spending
                    // time on recovery.
                    try { localStorage.removeItem('brainsync_user_email'); } catch (_) {}
                    setRecovering(false);
                })();
                return;
            }
        }

        if (isStandalone) return;

        // Browser-only silent sign-in for returning users.
        const savedEmail = localStorage.getItem('brainsync_user_email');
        const silentAttempted = sessionStorage.getItem('brainsync_silent_attempted');
        if (savedEmail && !silentAttempted) {
            sessionStorage.setItem('brainsync_silent_attempted', 'true');
            trySilentSignIn();
        }
    }, [loading, user, trySilentSignIn, navigate, checkAuth]);

    const fadeUp = {
        hidden: { opacity: 0, y: 20 },
        visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } })
    };

    // While we're checking auth state, recovering a TWA session, or
    // already know the user is signed in and a redirect to /dashboard is
    // queued - keep the HTML splash visible (don't dismiss it) and
    // render nothing in React. The splash is the loading indicator for
    // cold launches; mounting a React loader on top of it would cause a
    // visible double-loader flash on fast networks.
    if (loading || recovering || user) {
        return null;
    }

    // Final view (logged-out) - dismiss the splash so it fades into the
    // marketing hero. Same for the desktop marketing page below.
    if (typeof window !== 'undefined') {
        window.__brainsyncDismissSplash?.();
    }

    // Standalone (TWA / installed PWA) + mobile-web visitors get the
    // new "try one, see what happens" flow (introduced Aug 2026) —
    // designed to convert first-time visitors by letting them see the
    // AI magic on a real link BEFORE signing up. Desktop web keeps the
    // marketing landing below for SEO + paid acquisition.
    // Append ?app=1 in a regular browser to preview the flow. Append
    // ?legacy=1 to bypass and see the old AppLanding.
    const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    const forceAppPreview = params.get('app') === '1';
    const forceLegacyLanding = params.get('legacy') === '1';
    const isStandalone = detectStandalone();
    if ((isStandalone || forceAppPreview) && !forceLegacyLanding) {
        // Route into the new /try flow. Preserve any query params
        // (?signup=1 / ?next=... / share-target payload) so the flow
        // can react to them. Use <Navigate/> (declarative redirect)
        // rather than calling `navigate()` during render — calling
        // navigate() while the component is rendering crashes to a
        // white screen in the production TWA build. (Fix Aug 24 2026.)
        if (typeof window !== 'undefined' && window.location.pathname === '/') {
            return <Navigate to={'/try' + (window.location.search || '')} replace />;
        }
        return <AppLanding onSignIn={() => login()} />;
    }

    // Desktop (≥1024px): serve the new cream/emerald landing.
    // Auth flows (?signup=1 / ?login=1) stay on the new design — the
    // EmailAuthSheet is mounted at the root level below and overlays the
    // iframe. Only ?legacy=1 / ?mobile=1 escape to the classic landing.
    const forceLegacy = params.get('legacy') === '1' || params.get('mobile') === '1';
    if (!forceLegacy && typeof window !== 'undefined' && window.innerWidth >= 1024) {
        return (
            <>
                <LandingPageDesktop />
                <EmailAuthSheet
                    open={emailSheetOpen}
                    onOpenChange={(open) => {
                        setEmailSheetOpen(open);
                        if (!open && typeof window !== 'undefined') {
                            const sp = new URLSearchParams(window.location.search);
                            if (sp.get('signup') === '1' || sp.get('login') === '1') {
                                sp.delete('signup');
                                sp.delete('login');
                                const q = sp.toString();
                                window.history.replaceState({}, '', window.location.pathname + (q ? '?' + q : ''));
                            }
                        }
                    }}
                    initialMode={emailSheetMode}
                />
            </>
        );
    }

    // Mobile web (< 1024) — also route into the new /try flow. The
    // marketing sections below (feature scrolls, pricing, FAQ) still
    // serve tablets and any user who navigates to a non-`/` path.
    // Declarative <Navigate/> — see comment on the TWA branch above.
    if (!forceLegacy && typeof window !== 'undefined' && window.location.pathname === '/') {
        return <Navigate to={'/try' + (window.location.search || '')} replace />;
    }

    return (
        <div className="min-h-screen bg-[#FDFCF8] overflow-x-hidden" data-testid="landing-page">
            {/* Mobile web hero - same design as the TWA standalone view. The
                rest of the marketing content (platforms, features, pricing,
                FAQ, footer) flows below for users who scroll. Hidden on
                tablets and up where the original desktop hero takes over. */}
            <div className="md:hidden">
                <AppLanding embedded onSignIn={() => login()} />
            </div>

            {/* Subtle radial glow */}
            <div className="fixed inset-0 pointer-events-none hidden md:block" style={{
                background: 'radial-gradient(ellipse 60% 50% at 50% 0%, rgba(209,250,229,0.2) 0%, transparent 70%)'
            }} />

            {/* Nav (desktop only - the mobile AppLanding has its own
                visual hierarchy and doesn't need a fixed nav competing
                for vertical space) */}
            <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#FDFCF8]/80 border-b border-gray-200/40 hidden md:block">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-[#2E3A2F] flex items-center justify-center">
                            <Bookmark className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-heading font-bold text-base tracking-tight">BrainSync</span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-4">
                        <a
                            href="#pricing"
                            onClick={(e) => {
                                e.preventDefault();
                                document.querySelector('[aria-label="Pricing"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }}
                            className="hidden sm:inline text-sm font-medium text-gray-500 hover:text-[#2E3A2F] transition-colors px-3 py-1.5"
                            data-testid="nav-pricing-link"
                        >
                            Pricing
                        </a>
                        <Button 
                            onClick={login}
                            className="rounded-full px-5 py-2 text-sm font-semibold bg-[#2E3A2F] hover:bg-[#3F4F40] text-white transition-all hover:scale-105 active:scale-95 shadow-sm"
                            data-testid="nav-login-btn"
                        >
                            Get Started
                        </Button>
                    </div>
                </div>
            </nav>

            {/* ─── Desktop Hero (hidden on mobile) ─── */}
            <motion.section
                className="pt-24 lg:pt-32 pb-16 lg:pb-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden hidden md:block"
                initial="hidden" animate="visible"
            >
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                        {/* ── Left column: trust pill + headline + body + CTAs ── */}
                        <div className="lg:col-span-5 flex flex-col items-center lg:items-start text-center lg:text-left">
                            {/* Trust pill */}
                            <motion.div
                                className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-medium mb-6"
                                variants={fadeUp} custom={0}
                                data-testid="hero-trust-pill"
                            >
                                <Sparkles className="w-3.5 h-3.5" />
                                20 free AI saves · unlimited basic forever
                            </motion.div>

                            {/* Headline - with cycling green word */}
                            <motion.h1
                                className="font-heading text-4xl lg:text-[3.4rem] xl:text-6xl font-bold tracking-tight mb-5 text-[#2E3A2F]"
                                style={{ lineHeight: '1.08' }}
                                variants={fadeUp} custom={1}
                                data-testid="hero-headline"
                            >
                                Remember every{' '}
                                <RotatingWord />{' '}
                                you find.
                            </motion.h1>

                            {/* Body */}
                            <motion.p
                                className="text-base lg:text-lg text-gray-500 leading-relaxed max-w-lg mb-8"
                                variants={fadeUp} custom={2}
                                data-testid="hero-body-copy"
                            >
                                Drop any link - BrainSync transcribes, summarizes, and organizes it for you.
                                Build your second brain without the manual work.
                            </motion.p>

                            {/* CTA row - primary + secondary side by side, with
                                a small "Sign in" link tucked underneath. */}
                            <motion.div
                                className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto"
                                variants={fadeUp} custom={3}
                            >
                                <Button
                                    onClick={login}
                                    size="lg"
                                    className="w-full sm:w-auto rounded-full px-7 py-6 text-base font-bold bg-[#2E3A2F] hover:bg-[#3F4F40] text-white shadow-lg shadow-[#2E3A2F]/15 transition-all hover:scale-[1.02] active:scale-[0.98]"
                                    data-testid="hero-get-started-btn"
                                >
                                    Start for free
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </Button>
                                <Button
                                    type="button"
                                    onClick={() => { setEmailSheetMode('register'); setEmailSheetOpen(true); }}
                                    variant="outline"
                                    size="lg"
                                    className="w-full sm:w-auto rounded-full px-6 py-6 text-base font-semibold border-gray-200 bg-white text-[#2E3A2F] hover:bg-gray-50 transition-all"
                                    data-testid="hero-email-signup-link"
                                >
                                    Sign up with email
                                </Button>
                            </motion.div>

                            <motion.div
                                className="flex items-center gap-3 mt-4 text-[12px] text-gray-500"
                                variants={fadeUp} custom={4}
                            >
                                <span className="font-mono uppercase tracking-[0.18em] text-gray-400">No credit card required</span>
                                <span className="text-gray-300">·</span>
                                <button
                                    type="button"
                                    onClick={() => { setEmailSheetMode('login'); setEmailSheetOpen(true); }}
                                    className="hover:text-emerald-700 font-medium"
                                    data-testid="hero-email-signin-link"
                                >
                                    Sign in
                                </button>
                            </motion.div>
                        </div>

                        {/* ── Right column: product mockup, gently floating ── */}
                        <motion.div
                            className="lg:col-span-7 relative"
                            variants={fadeUp} custom={5}
                            data-testid="hero-dashboard-mockup"
                        >
                            {/* Soft radial glow behind the mockup so it
                                feels lifted off the page without a heavy
                                drop shadow. */}
                            <div
                                aria-hidden
                                className="absolute -inset-6 rounded-[2rem] blur-3xl opacity-50"
                                style={{
                                    background:
                                        'radial-gradient(circle at 30% 30%, rgba(16,185,129,0.18), transparent 60%)',
                                }}
                            />
                            <motion.div
                                className="relative"
                                animate={{ y: [0, -10, 0] }}
                                transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                            >
                                <DashboardMockup />
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </motion.section>

            {/* ─── Platform Strip ─── */}
            <section className="py-10 sm:py-14 px-4 sm:px-6 border-y border-gray-100">
                <div className="max-w-4xl mx-auto">
                    <p className="text-center text-[10px] sm:text-xs font-mono uppercase tracking-[0.2em] text-gray-400 mb-6">Works with any content source</p>
                    <div className="flex flex-wrap justify-center gap-x-6 sm:gap-x-8 gap-y-3">
                        {[
                            { name: 'YouTube', icon: Play },
                            { name: 'TikTok', icon: Video },
                            { name: 'Instagram', icon: Smartphone },
                            { name: 'Articles', icon: FileText },
                            { name: 'Recipes', icon: UtensilsCrossed },
                            { name: 'Podcasts', icon: Brain },
                        ].map((p, i) => (
                            <motion.div 
                                key={p.name}
                                className="flex items-center gap-2 text-gray-400 hover:text-gray-600 transition-colors"
                                initial={{ opacity: 0 }}
                                whileInView={{ opacity: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.06 }}
                            >
                                <p.icon className="w-4 h-4" />
                                <span className="text-sm font-medium">{p.name}</span>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── How It Works ─── */}
            <section className="py-16 sm:py-24 px-4 sm:px-6">
                <div className="max-w-5xl mx-auto">
                    <motion.div 
                        className="text-center mb-12 sm:mb-16"
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <p className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.2em] text-gray-400 mb-3">How it works</p>
                        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#2E3A2F]">
                            Three steps to total recall
                        </h2>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                        {[
                            { num: '01', icon: Globe, title: 'Paste any link', desc: 'YouTube, TikTok, Instagram, articles, recipes - anything on the web.' },
                            { num: '02', icon: Brain, title: 'AI does the work', desc: 'Whisper transcribes audio. GPT summarizes content. Tags are auto-generated.' },
                            { num: '03', icon: Search, title: 'Find it instantly', desc: 'Full-text search, AI-powered questions, organized collections.' }
                        ].map((step, i) => (
                            <motion.div
                                key={step.num}
                                className="relative bg-white border border-gray-100 rounded-2xl p-6 hover:border-emerald-200 hover:shadow-[0_8px_30px_-12px_rgba(0,0,0,0.08)] transition-all duration-300 group"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1, duration: 0.5 }}
                            >
                                <span className="absolute top-4 right-4 font-heading text-5xl font-bold text-gray-100 group-hover:text-emerald-100 transition-colors select-none">{step.num}</span>
                                <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center mb-5 group-hover:bg-emerald-100 transition-colors">
                                    <step.icon className="w-5 h-5 text-emerald-600" />
                                </div>
                                <h3 className="font-heading text-base sm:text-lg font-semibold text-[#2E3A2F] mb-2">{step.title}</h3>
                                <p className="text-sm text-gray-500 leading-relaxed">{step.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── Features ─── */}
            <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white">
                <div className="max-w-5xl mx-auto">
                    <motion.div 
                        className="text-center mb-12 sm:mb-16"
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <p className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.2em] text-gray-400 mb-3">Features</p>
                        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#2E3A2F]">
                            Everything you need to<br />
                            <span className="text-emerald-600">never forget anything</span>
                        </h2>
                    </motion.div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                        <FeatureCard icon={Sparkles} title="AI Summaries" description="GPT reads every page and gives you the key takeaways in seconds." accent delay={0} className="sm:col-span-2 lg:col-span-1 lg:row-span-2" />
                        <FeatureCard icon={Brain} title="Video Transcription" description="Whisper converts any video's audio into searchable text." delay={0.05} />
                        <FeatureCard icon={Search} title="Full-text Search" description="Search across every transcript, summary, and note you've saved." delay={0.1} />
                        <FeatureCard icon={MessageCircle} title="Ask BrainSync" description="Chat with an AI that knows everything in your bookmarks." delay={0.15} />
                        <FeatureCard icon={UtensilsCrossed} title="Smart Recipes" description="Video recipes auto-extracted into ingredients and step-by-step instructions." delay={0.2} />
                        <FeatureCard icon={Video} title="Video Import" description="Download and play videos directly - with animated preview thumbnails." delay={0.1} />
                        <FeatureCard icon={Tag} title="Auto-tagging" description="AI generates tags from your content. Organize with custom collections." delay={0.15} />
                    </div>
                </div>
            </section>

            {/* ─── Social Proof ─── */}
            <section className="py-16 sm:py-24 px-4 sm:px-6">
                <div className="max-w-5xl mx-auto">
                    <motion.div 
                        className="text-center mb-12"
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#2E3A2F] mb-4">
                            Built for how you actually<br className="hidden sm:block" /> consume content
                        </h2>
                        <p className="text-base text-gray-500 max-w-lg mx-auto">
                            Whether you're saving recipes from TikTok, research papers, or podcast clips - BrainSync handles it all.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8 mb-16">
                        <StatItem value="50+" label="Supported platforms" delay={0} />
                        <StatItem value="<5s" label="To summarize any page" delay={0.1} />
                        <StatItem value="20" label="Free AI saves to start" delay={0.2} />
                        <StatItem value="∞" label="Basic saves, always free" delay={0.3} />
                    </div>

                    {/* Use case cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {[
                            { title: 'For Researchers', items: ['Save academic papers', 'Full-text search across sources', 'AI-generated summaries'], color: 'bg-blue-50 border-blue-100', textColor: 'text-blue-700', icon: FileText },
                            { title: 'For Content Creators', items: ['Bookmark inspiration videos', 'Transcribe & reference later', 'Organize by project'], color: 'bg-purple-50 border-purple-100', textColor: 'text-purple-700', icon: Video },
                            { title: 'For Home Cooks', items: ['Save recipe videos from TikTok', 'Auto-extract ingredients', 'Step-by-step instructions'], color: 'bg-orange-50 border-orange-100', textColor: 'text-orange-700', icon: UtensilsCrossed }
                        ].map((uc, i) => (
                            <motion.div
                                key={uc.title}
                                className={`rounded-2xl border p-5 sm:p-6 ${uc.color}`}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.1 }}
                            >
                                <div className="flex items-center gap-2 mb-4">
                                    <uc.icon className={`w-5 h-5 ${uc.textColor}`} />
                                    <h3 className={`font-heading font-semibold text-sm ${uc.textColor}`}>{uc.title}</h3>
                                </div>
                                <ul className="space-y-2">
                                    {uc.items.map((item) => (
                                        <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                                            <ChevronRight className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${uc.textColor}`} />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── Pricing ─── */}
            <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gradient-to-b from-white to-gray-50/40" aria-label="Pricing">
                <div className="max-w-5xl mx-auto">
                    <motion.div 
                        className="text-center mb-12 sm:mb-14"
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <p className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.2em] text-gray-400 mb-3">Pricing</p>
                        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#2E3A2F] mb-3">
                            Start free.<br className="sm:hidden" /> Upgrade when you want more&nbsp;AI.
                        </h2>
                        <p className="text-sm sm:text-base text-gray-500 max-w-lg mx-auto">
                            Unlimited Basic saves on every plan. Pay only for AI-processed bookmarks.
                        </p>
                        {PROMO_ACTIVE && (
                            <div className="mt-6 inline-flex flex-col items-center gap-2" data-testid="landing-promo-banner">
                                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold uppercase tracking-[0.15em]">
                                    <Sparkles className="w-3.5 h-3.5" />
                                    {PROMO_BADGE}
                                </span>
                                <p className="text-sm text-[#2E3A2F] font-medium max-w-md">
                                    {PROMO_LOCK_LINE}
                                </p>
                                <p className="text-xs font-semibold text-emerald-700" data-testid="landing-promo-urgency">
                                    {PROMO_URGENCY}
                                </p>
                            </div>
                        )}
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
                        {[
                            {
                                name: 'BrainSync Basic', price: '$0', interval: 'forever',
                                tagline: 'Perfect to try it out',
                                features: [
                                    { text: '20 Premium AI saves (lifetime)', strong: true },
                                    { text: 'Unlimited Basic saves', strong: true },
                                    { text: '10 Ask BrainSync / month' },
                                    { text: 'Videos up to 10 min · 50 min/mo' },
                                    { text: 'Manual tags & collections' },
                                    { text: 'Full-text search' },
                                ],
                                cta: 'Start for free',
                                style: 'bg-white border-gray-200',
                            },
                            {
                                name: 'BrainSync Plus', price: PRICING.plus.monthly, wasPrice: PRICING.plus.monthlyWas, interval: 'per month',
                                tagline: 'Most popular',
                                features: [
                                    { text: '50 Premium AI saves / month', strong: true },
                                    { text: 'Unlimited Basic saves', strong: true },
                                    { text: '50 Ask BrainSync / month' },
                                    { text: 'Videos up to 30 min · 250 min/mo' },
                                    { text: '100 MB attachments · 2 GB storage' },
                                    { text: `${PRICING.plus.yearly} / year (${ANNUAL_SAVING_PCT}% off)` },
                                ],
                                cta: 'Get Plus',
                                style: 'bg-[#2E3A2F] border-[#2E3A2F] text-white shadow-xl',
                                popular: true,
                            },
                            {
                                name: 'BrainSync Pro', price: PRICING.pro.monthly, wasPrice: PRICING.pro.monthlyWas, interval: 'per month',
                                tagline: 'For power users',
                                features: [
                                    { text: '200 Premium AI saves / month', strong: true },
                                    { text: 'Unlimited Basic saves', strong: true },
                                    { text: '150 Ask BrainSync / month' },
                                    { text: 'Videos up to 90 min · 600 min/mo' },
                                    { text: '500 MB attachments · 10 GB storage' },
                                    { text: `${PRICING.pro.yearly} / year (${ANNUAL_SAVING_PCT}% off)` },
                                ],
                                cta: 'Go Pro',
                                style: 'bg-white border-gray-200',
                            },
                        ].map((plan, i) => {
                            const isPop = plan.popular;
                            return (
                                <motion.div
                                    key={plan.name}
                                    className={`relative rounded-3xl border p-6 sm:p-7 flex flex-col ${plan.style}`}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.08, duration: 0.5 }}
                                    data-testid={`landing-pricing-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                    {isPop && (
                                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider">
                                            {plan.tagline}
                                        </span>
                                    )}
                                    <p className={`text-[10px] font-mono uppercase tracking-[0.2em] mb-3 ${isPop ? 'text-emerald-300' : 'text-gray-400'}`}>
                                        {isPop ? 'Most popular' : plan.tagline}
                                    </p>
                                    <h3 className={`font-heading text-xl sm:text-2xl font-bold mb-4 ${isPop ? 'text-white' : 'text-[#2E3A2F]'}`}>
                                        {plan.name}
                                    </h3>
                                    <div className="mb-5">
                                        {PROMO_ACTIVE && plan.wasPrice && (
                                            <span
                                                className={`mr-2 text-xl sm:text-2xl font-medium line-through ${isPop ? 'text-white/40' : 'text-gray-400'}`}
                                                data-testid={`landing-pricing-was-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
                                            >
                                                {plan.wasPrice}
                                            </span>
                                        )}
                                        <span className={`text-4xl sm:text-5xl font-bold tracking-tight ${isPop ? 'text-white' : 'text-[#2E3A2F]'}`}>
                                            {plan.price}
                                        </span>
                                        <span className={`ml-1.5 text-sm ${isPop ? 'text-white/50' : 'text-gray-400'}`}>/ {plan.interval}</span>
                                        {PROMO_ACTIVE && plan.wasPrice && (
                                            <p className={`mt-2 text-xs font-medium ${isPop ? 'text-emerald-300' : 'text-emerald-600'}`}>
                                                {PROMO_LOCK_SHORT}
                                            </p>
                                        )}
                                    </div>
                                    <ul className="space-y-2.5 mb-7 flex-1">
                                        {plan.features.map((f, idx) => (
                                            <li key={f.text} className={`flex items-start gap-2 text-sm ${isPop ? 'text-white/80' : 'text-gray-600'}`}>
                                                <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${isPop ? 'text-emerald-300' : 'text-emerald-500'}`} />
                                                <span className={f.strong ? (isPop ? 'text-white font-medium' : 'text-[#2E3A2F] font-medium') : ''}>
                                                    {f.text}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                    <Button
                                        onClick={login}
                                        size="lg"
                                        className={`w-full rounded-full font-semibold transition-all hover:scale-[1.02] active:scale-[0.98] ${
                                            isPop
                                                ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/20'
                                                : 'bg-[#2E3A2F] hover:bg-[#3F4F40] text-white'
                                        }`}
                                        data-testid={`landing-pricing-cta-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
                                    >
                                        {plan.cta}
                                        <ArrowRight className="w-4 h-4 ml-1.5" />
                                    </Button>
                                </motion.div>
                            );
                        })}
                    </div>

                    <p className="text-center text-xs text-gray-400 mt-6">
                        Save {ANNUAL_SAVING_PCT}% with annual billing. Cancel anytime from your account settings.
                    </p>
                </div>
            </section>

            {/* ─── FAQ Section (SEO + GEO) ─── */}
            <section className="py-16 sm:py-24 px-4 sm:px-6" aria-label="Frequently Asked Questions">
                <div className="max-w-3xl mx-auto">
                    <motion.div
                        className="text-center mb-12"
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <p className="text-[10px] sm:text-xs font-mono uppercase tracking-[0.2em] text-gray-400 mb-3">FAQ</p>
                        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#2E3A2F]">
                            Common questions
                        </h2>
                    </motion.div>
                    <div className="space-y-4">
                        {[
                            { q: 'What is BrainSync?', a: 'BrainSync is an AI-powered bookmarking platform that saves any link and provides instant AI summaries, video transcriptions, and smart organization. Think of it as your AI second brain.' },
                            { q: 'How does BrainSync transcribe videos?', a: 'BrainSync uses OpenAI Whisper to extract and transcribe audio from YouTube, TikTok, Instagram, Facebook videos, and more. The transcript is then summarized by GPT for quick reading.' },
                            { q: 'What content can I save?', a: 'Anything with a URL - YouTube videos, TikTok reels, Instagram posts, articles, recipes, podcasts, Google Maps places, and more. You can also upload images and documents for AI analysis.' },
                            { q: 'Is BrainSync free?', a: "Yes. You can save unlimited bookmarks for free - titles, thumbnails, and your own notes/tags. AI features (summaries, video transcription, auto-categorization) are free for your first 20 bookmarks. After that you can still save anything, or upgrade to Pro for ongoing AI processing." },
                            { q: 'What\'s the difference between a Basic and a Premium bookmark?', a: "A Premium bookmark is fully AI-processed - Whisper transcribes videos, GPT summarizes the content, and smart tags + collections are auto-generated. A Basic bookmark stores only the title, thumbnail, and your own notes. You can upgrade any Basic bookmark to Premium later by clicking Reprocess (requires available quota)." },
                            { q: 'Can I search across all my bookmarks?', a: 'Absolutely. BrainSync offers full-text search across every transcript, summary, note, and tag. You can also ask the AI assistant questions about your entire collection.' },
                            { q: 'Does BrainSync work on mobile?', a: 'Yes. BrainSync is available as a web app, an Android app, and can be added to your iPhone home screen as a PWA. Share links directly from any app on your phone.' },
                        ].map((faq, i) => (
                            <motion.details
                                key={faq.q}
                                className="group bg-white border border-gray-100 rounded-2xl overflow-hidden"
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.05 }}
                            >
                                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm sm:text-base font-semibold text-[#2E3A2F] hover:bg-gray-50/50 transition-colors list-none">
                                    {faq.q}
                                    <ChevronRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 shrink-0 ml-2" />
                                </summary>
                                <div className="px-5 pb-4 text-sm text-gray-500 leading-relaxed">
                                    {faq.a}
                                </div>
                            </motion.details>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── Trust Strip ─── */}
            <section className="py-10 px-4 sm:px-6 bg-gray-50/50 border-y border-gray-100">
                <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-6 sm:gap-10 text-sm text-gray-400">
                    <div className="flex items-center gap-2"><Shield className="w-4 h-4" /><span>Google OAuth</span></div>
                    <div className="flex items-center gap-2"><Globe className="w-4 h-4" /><span>Works on any device</span></div>
                    <div className="flex items-center gap-2"><Zap className="w-4 h-4" /><span>No installation needed</span></div>
                    <div className="flex items-center gap-2"><Smartphone className="w-4 h-4" /><span>PWA + Android app</span></div>
                </div>
            </section>

            {/* ─── Final CTA ─── */}
            <section className="py-16 sm:py-24 px-4 sm:px-6">
                <div className="max-w-2xl mx-auto">
                    <motion.div
                        className="relative overflow-hidden bg-[#2E3A2F] rounded-3xl p-8 sm:p-12 text-center"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <div className="absolute inset-0 pointer-events-none" style={{
                            background: 'radial-gradient(circle at 50% 0%, rgba(16,185,129,0.15) 0%, transparent 60%)'
                        }} />
                        <div className="relative">
                            <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-6">
                                <Bookmark className="w-7 h-7 text-emerald-300" />
                            </div>
                            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-3">
                                Start building your<br />second brain today
                            </h2>
                            <p className="text-white/50 text-sm sm:text-base mb-8 max-w-sm mx-auto">
                                20 free AI bookmarks, unlimited basic saves forever. No credit card - ever.
                            </p>
                            <Button 
                                onClick={login}
                                size="lg"
                                className="w-full sm:w-auto rounded-full px-8 py-6 text-base font-semibold bg-white text-[#2E3A2F] hover:bg-white/90 shadow-lg transition-all hover:scale-105 active:scale-95"
                                data-testid="cta-get-started-btn"
                            >
                                Get started free
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-gray-100 py-6 px-4 sm:px-6 bg-white">
                <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-md bg-[#2E3A2F] flex items-center justify-center">
                            <Bookmark className="w-3 h-3 text-white" />
                        </div>
                        <span className="font-heading font-semibold text-sm text-gray-900">BrainSync</span>
                    </div>
                    <p className="text-xs text-gray-400 text-center sm:text-right" data-testid="footer-operator-line">
                        Your AI-powered second brain · brainsync.pro is operated by Aniral Ltd
                    </p>
                </div>
            </footer>

            {/* Email/password auth - shared modal opened from desktop hero
                + sticky nav. Mobile traffic uses the AppLanding component
                which mounts its own copy.
                On close: if the sheet was opened by ?signup=1 / ?login=1 from
                the new desktop landing, strip the param and navigate to /
                so the viewport gate re-renders LandingPageDesktop. Prevents
                users getting "stuck" on the legacy landing after dismissing. */}
            <EmailAuthSheet
                open={emailSheetOpen}
                onOpenChange={(open) => {
                    setEmailSheetOpen(open);
                    if (!open && typeof window !== 'undefined') {
                        const sp = new URLSearchParams(window.location.search);
                        if (sp.get('signup') === '1' || sp.get('login') === '1') {
                            sp.delete('signup');
                            sp.delete('login');
                            const q = sp.toString();
                            const url = window.location.pathname + (q ? '?' + q : '');
                            // On desktop, replaceState alone won't re-trigger the
                            // viewport gate render — force a full navigation so the
                            // new landing re-renders cleanly.
                            if (window.innerWidth >= 1024) {
                                window.location.replace(url);
                            } else {
                                window.history.replaceState({}, '', url);
                            }
                        }
                    }
                }}
                initialMode={emailSheetMode}
            />
        </div>
    );
};

export default LandingPage;
